import { api } from './client'
import type { StockNoteRollbackRefs } from './stock-note-ref'

// ============================================================================
// Kho vật tư nông nghiệp — lô + PNK + PXK (pattern items M-N).
// Status sống trên phiếu: pending|approved|rejected|cancelled.
// Khác kho bao bì: có HSD/NSX trên lô; PXK item có grower_id (hộ trồng).
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

// `from_stocktake_*` chỉ do hệ thống sinh khi duyệt phiếu kiểm kê thực tế —
// người dùng không chọn được khi tạo phiếu (xem AGRI_IN_REASONS/AGRI_OUT_REASONS
// trong agri-note-schemas.ts).
export type AgriMaterialLotInReason =
    'from_supplier' | 'adjustment' | 'from_stocktake_surplus'
export type AgriMaterialLotOutReason =
    'for_use' | 'discard' | 'adjustment' | 'from_stocktake_shortage'

export interface AgriMaterialLot {
    id: string
    code: string
    agricultural_material_id: string
    material_name?: string
    material_unit?: string
    /** Vật tư của lô có theo dõi tồn không — lô của vật tư đã bỏ tick không
     *  nhập/xuất được nữa nên picker ẩn đi. */
    material_is_external?: boolean
    warehouse_id: string
    warehouse_name?: string
    manufactured_date?: string | null
    expiry_date?: string | null
    supplier_name?: string | null
    quantity_available: number
    status: string
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface AgriMaterialStockInNoteItem {
    id: string
    note_id: string
    agri_material_lot_id: string
    lot_code?: string
    material_name?: string
    material_unit?: string
    quantity: number
    created_at: string
}

export interface AgriMaterialStockOutNoteItem extends AgriMaterialStockInNoteItem {
    grower_id?: string | null
    grower_name?: string
}

export interface AgriMaterialStockInNote extends StockNoteRollbackRefs {
    id: string
    code: string
    status: NoteStatus
    reason: AgriMaterialLotInReason
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    items?: AgriMaterialStockInNoteItem[]
}

export interface AgriMaterialStockOutNote extends Omit<
    AgriMaterialStockInNote,
    'reason' | 'items'
> {
    reason: AgriMaterialLotOutReason
    /** Nhật ký chăm sóc sinh ra phiếu này — phiếu đó không rollback được. */
    care_log_id?: string | null
    items?: AgriMaterialStockOutNoteItem[]
}

export interface AgriMaterialLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
}

// ============================================================================
// Request shapes
// ============================================================================

export type CreateInNoteItemInput =
    | { agri_material_lot_id: string; quantity: number }
    | {
          new_lot_material_id: string
          new_lot_warehouse_id?: string
          new_lot_code?: string
          new_lot_supplier_name?: string
          new_lot_manufactured_date?: string
          new_lot_expiry_date?: string
          quantity: number
      }

export interface CreateInNoteRequest {
    reason: AgriMaterialLotInReason
    notes?: string
    items: CreateInNoteItemInput[]
}

export type UpdateInNoteRequest = CreateInNoteRequest

export type CreateOutNoteItemInput =
    | { agri_material_lot_id: string; grower_id?: string; quantity: number }
    | {
          agricultural_material_id: string
          warehouse_id: string
          grower_id?: string
          quantity: number
      }

export interface CreateOutNoteRequest {
    reason: AgriMaterialLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

export type UpdateOutNoteRequest = CreateOutNoteRequest

export interface ListLotsParams {
    material_id?: string
    warehouse_id?: string
    search?: string
    only_available?: '1' | undefined
    /** '1' → gồm cả lô ảo 'STOCK' (qty-mode). Picker xuất truyền để chọn xuất
     *  qty-mode; danh sách lô bỏ trống (ẩn STOCK). */
    include_virtual?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/agricultural-material-lots'

export const agriMaterialLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<AgriMaterialLot[]>(BASE, { params }),
    getLot: (id: string) => api.get<AgriMaterialLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) =>
        api.get<AgriMaterialLotHistoryEntry[]>(`${BASE}/${id}/history`, {
            params,
        }),

    // Stock-in notes (PNK)
    listInNotes: (params?: ListNotesParams) =>
        api.get<AgriMaterialStockInNote[]>(`${BASE}/stock-in-notes`, {
            params,
        }),
    getInNote: (id: string) =>
        api.get<AgriMaterialStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<AgriMaterialStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<AgriMaterialStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),
    /** Rollback PNK đã duyệt → BE sinh PXK điều chỉnh, trả phiếu vừa sinh. */
    rollbackInNote: (id: string) =>
        api.post<AgriMaterialStockOutNote>(
            `${BASE}/stock-in-notes/${id}/rollback`,
        ),

    // Stock-out notes (PXK)
    listOutNotes: (params?: ListNotesParams) =>
        api.get<AgriMaterialStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    getOutNote: (id: string) =>
        api.get<AgriMaterialStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<AgriMaterialStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<AgriMaterialStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),
    /** Rollback PXK đã duyệt → BE sinh PNK điều chỉnh, trả phiếu vừa sinh. */
    rollbackOutNote: (id: string) =>
        api.post<AgriMaterialStockInNote>(
            `${BASE}/stock-out-notes/${id}/rollback`,
        ),
}
